import React from 'react';
import { motion } from 'framer-motion';
import type { ViewType } from '../data/homepageData';

interface FinalCTAProps {
  view: ViewType;
}

/**
 * Bottom CTA section with a subtle radial glow background.
 * Copy and auth link adapt to the current view.
 */
const FinalCTA: React.FC<FinalCTAProps> = ({ view }) => {
  const authLink = view === 'creator' ? '/auth?type=creator' : '/auth?type=brand';

  const headline =
    view === 'creator' ? (
      <>
        Ready to <span className="cv-text-gradient">start earning</span>?
      </>
    ) : (
      <>
        Ready to <span className="cv-text-gradient">scale your UGC</span>?
      </>
    );

  const subtext =
    view === 'creator'
      ? 'Join thousands of creators earning real money from their content.'
      : 'Join 500+ brands running high-ROI UGC campaigns.';

  const ctaLabel =
    view === 'creator' ? 'Create Your Account' : 'Start 3-day free trial';

  return (
    <section
      className="relative text-center overflow-hidden"
      style={{ padding: 'clamp(64px, 10vw, 140px) 0' }}
    >
      {/* Radial glow background */}
      <div className="cv-final-cta-bg" aria-hidden="true" />

      <div className="relative z-[2] w-full max-w-[1200px] mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <h2
            className="font-extrabold tracking-tight mb-5"
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-3xl)',
              letterSpacing: '-0.03em',
            }}
          >
            {headline}
          </h2>

          <p
            className="mx-auto mb-10"
            style={{
              fontSize: 'var(--text-lg)',
              color: 'var(--cv-text-muted)',
              maxWidth: '500px',
            }}
          >
            {subtext}
          </p>

          {/* CTA buttons */}
          <div className="cv-hero-cta-group flex items-center justify-center gap-4 flex-wrap mb-6">
            <a
              href={authLink}
              className="cv-btn-primary"
              style={{ padding: '16px 36px', fontSize: '16px' }}
            >
              {ctaLabel}
              <svg
                width={16}
                height={16}
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M3 8h10M9 4l4 4-4 4" />
              </svg>
            </a>

            <a
              href="/pricing"
              className="cv-btn-secondary"
              style={{ padding: '16px 36px', fontSize: '16px' }}
              target="_blank"
              rel="noopener noreferrer"
            >
              View Pricing
            </a>
          </div>

          <p
            style={{ fontSize: '13px', color: 'var(--cv-text-faint)', marginTop: '20px' }}
          >
            {view === 'brand'
              ? '3 days free · Cancel anytime · No charge for 3 days'
              : 'No credit card required · Free to get started'}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default FinalCTA;
